import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import firebaseService from '../Services/firebaseService';
import { log } from '../Utils/logger';
import { useI18n } from '../i18n/I18nProvider';

interface FeedbackSectionProps {
    filesCount: number;
    isSender: boolean;
    sessionId: string;
}

function ShareButtons() {
    const mainUrl = window.location.origin;
    const [copied, setCopied] = useState(false);
    const { t } = useI18n();

    const handleCopy = () => {
        navigator.clipboard.writeText(mainUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'FilePlease',
                    text: t('home.title'),
                    url: mainUrl,
                });
            } catch (err) {
                if ((err as { name?: string }).name !== 'AbortError') {
                    log('Error sharing:', err);
                }
            }
        } else {
            handleCopy();
        }
    };

    const isShareSupported =
        typeof navigator !== 'undefined' && !!navigator.share;

    return (
        <div className="mt-6 flex flex-col items-center animate-fade-in">
            <Typography
                variant="body2"
                className="text-gray-700 mb-4 font-medium italic"
            >
                {t('feedback.enjoying')}
            </Typography>
            <div className="flex gap-3">
                {isShareSupported && (
                    <Button
                        variant="contained"
                        size="medium"
                        startIcon={<ShareIcon />}
                        onClick={handleShare}
                        sx={{
                            borderRadius: '4px',
                            textTransform: 'none',
                            px: 4,
                            backgroundColor: '#1976d2',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                            '&:hover': {
                                backgroundColor: '#115293',
                                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.3)',
                            },
                        }}
                    >
                        {t('feedback.share')}
                    </Button>
                )}
                <Tooltip
                    title={t('feedback.copiedTooltip')}
                    open={copied}
                    slotProps={{
                        tooltip: {
                            sx: {
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                fontWeight: 600,
                                border: '1px solid #bbdefb',
                            },
                        },
                        arrow: {
                            sx: {
                                color: '#e3f2fd',
                            },
                        },
                    }}
                >
                    <Button
                        variant="outlined"
                        size="medium"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopy}
                        sx={{
                            borderRadius: '4px',
                            textTransform: 'none',
                            px: 3,
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                borderColor: '#115293',
                            },
                        }}
                    >
                        {copied ? t('feedback.copied') : t('feedback.copyLink')}
                    </Button>
                </Tooltip>
            </div>
        </div>
    );
}

export default function FeedbackSection({
    filesCount,
    isSender,
    sessionId,
}: FeedbackSectionProps) {
    const [rating, setRating] = useState<number>(0);
    const [ratingSent, setRatingSent] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [isSent, setIsSent] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const { t } = useI18n();
    const hasFeedbackText = feedbackText.trim().length >= 1;

    const handleRating = (value: number) => {
        setRating(value);
        if (!ratingSent) {
            setRatingSent(true);
            firebaseService.sendRating({
                is_sender: isSender,
                rating: value,
                ua: window.navigator.userAgent,
                session_id: sessionId,
            });
        }
    };

    const handleSend = async () => {
        if (rating === 0 || !hasFeedbackText) return;
        setIsSending(true);
        try {
            await firebaseService.sendFeedback({
                files_count: filesCount,
                is_sender: isSender,
                rating,
                text: feedbackText,
                ua: window.navigator.userAgent,
                session_id: sessionId,
            });
            setIsSent(true);
        } catch (err) {
            log('Error sending feedback:', err);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Box className="mt-4 pt-4 border-t border-gray-100 flex flex-col items-center w-full">
            <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <IconButton
                        key={star}
                        onClick={() => handleRating(star)}
                        sx={{
                            color: star <= rating ? '#FFD700' : '#bdbdbd',
                            p: 0.5,
                            '&:hover': {
                                backgroundColor: 'transparent',
                            },
                        }}
                    >
                        {star <= rating ? (
                            <StarIcon sx={{ fontSize: 24 }} />
                        ) : (
                            <StarBorderIcon sx={{ fontSize: 24 }} />
                        )}
                    </IconButton>
                ))}
            </div>

            {rating === 0 && (
                <Typography
                    variant="body2"
                    className="text-gray-500 mb-4 animate-fade-in"
                >
                    {t('feedback.rateService')}
                </Typography>
            )}

            {rating > 0 && (
                <div className="flex flex-col items-center w-full animate-fade-in">
                    <Typography variant="body1" className="text-gray-700 mb-4">
                        {t('feedback.thanksVoting')}
                    </Typography>

                    <Collapse in={!isSent} className="w-full">
                        <Box sx={{ width: '100%', mb: 2 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder={
                                    rating <= 3
                                        ? t('feedback.lowRatingPlaceholder')
                                        : t('feedback.highRatingPlaceholder')
                                }
                                value={feedbackText}
                                onChange={(e) =>
                                    setFeedbackText(e.target.value)
                                }
                                variant="outlined"
                                disabled={isSending}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: '#f9f9f9',
                                    },
                                }}
                            />
                            <Button
                                fullWidth
                                onClick={handleSend}
                                variant="contained"
                                disabled={isSending || !hasFeedbackText}
                                sx={{
                                    mt: 2,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    py: 1,
                                    backgroundColor: '#1976d2',
                                    '&:hover': {
                                        backgroundColor: '#115293',
                                    },
                                }}
                            >
                                {isSending
                                    ? t('feedback.sending')
                                    : t('feedback.send')}
                            </Button>
                        </Box>
                    </Collapse>

                    <Collapse in={isSent} className="w-full">
                        <Typography
                            variant="h6"
                            className="text-green-600 font-medium my-4 p-4 bg-green-50 rounded-xl border border-green-100 text-center"
                        >
                            {t('feedback.thankYou')}
                        </Typography>
                    </Collapse>

                    {rating >= 4 && <ShareButtons />}
                </div>
            )}
        </Box>
    );
}
